import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import styles from './confirmDialog.module.css'

/**
 * A confirmation step in front of an action that is hard to take back.
 *
 * Exists because archiving used to happen on a single click of a "⋯" button — a glyph that means
 * "options" everywhere else in software, so people pressed it to see what was there and lost the
 * task instead. A destructive action needs a name and a question, not an icon.
 *
 * Rendered into `body`: the drawer it is opened from is itself a scroll container, and anything
 * absolutely positioned inside one gets clipped by it.
 */
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel = 'Anuluj',
  tone = 'danger',
  onConfirm,
  onCancel,
}: {
  open: boolean
  title: string
  body?: string | undefined
  confirmLabel: string
  cancelLabel?: string | undefined
  tone?: 'danger' | 'normal' | undefined
  onConfirm: () => void
  onCancel: () => void
}): React.JSX.Element | null {
  const confirmRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return

    // Focus lands on the confirm button, but Escape and the backdrop are always one action away —
    // the fast path out of a dialog you opened by accident must not require aiming.
    confirmRef.current?.focus()

    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault()
        // Stops the drawer behind from also closing on the same Escape.
        event.stopPropagation()
        onCancel()
      }
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [open, onCancel])

  if (!open) return null

  return createPortal(
    <div className={styles.scrim}>
      {/* A real button rather than a div with onClick: clicking the backdrop is a shortcut, and a
          shortcut that only exists for a mouse is not a shortcut for everybody. Escape does the
          same thing, and this element is what makes that reachable by Tab too. */}
      <button
        type="button"
        className={styles.scrimButton}
        aria-label="Zamknij bez zmian"
        onClick={onCancel}
      />
      <div className={styles.dialog} role="alertdialog" aria-modal="true" aria-label={title}>
        <p className={styles.title}>{title}</p>
        {body && <p className={styles.body}>{body}</p>}
        <div className={styles.actions}>
          <button type="button" className={styles.cancel} onClick={onCancel}>
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            type="button"
            className={tone === 'danger' ? styles.confirmDanger : styles.confirm}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
